/**
 * Sink Node Component for React Flow
 *
 * Renders outflow destinations on the boundary edge.
 * Blue/slate styling to indicate value leaving the system.
 */
import { memo, useState } from "react";
import { Handle, Position, type NodeProps } from "reactflow";
import type { ValueFlowNodeData } from "../../../../utils/schemaToReactFlow";
import { useHoverContext } from "../HoverContext";
import { NodeHoverLabel } from "./NodeHoverLabel";
import { NodeMetadataTooltip } from "./NodeMetadataTooltip";
import { ObservableBadgesContainer } from "./ObservableBadge";

/**
 * Resolve spec_icon path to actual URL
 */
const resolveSpecIconUrl = (specIcon: string): string => {
  const filename = specIcon.split("/").pop();
  return `/assets/sa-elements/${filename}`;
};

/**
 * Default wallet icon SVG
 */
const WalletIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M20 12V8H6a2 2 0 01-2-2c0-1.1.9-2 2-2h12v4" />
    <path d="M4 6v12a2 2 0 002 2h14v-6" />
    <path d="M18 12a2 2 0 100 4 2 2 0 000-4z" />
  </svg>
);

/**
 * Outflow arrow icon
 */
const OutflowIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 19V5M5 12l7-7 7 7" />
  </svg>
);

/**
 * Loop icon for internal loop-back sinks
 */
const LoopIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M21 12a9 9 0 11-6.219-8.56" />
    <path d="M21 3v5h-5" />
  </svg>
);

/**
 * Get icon based on sink type
 */
const getIcon = (sinkType?: string) => {
  if (sinkType === "satchel_wallet") return <WalletIcon />;
  if (sinkType === "internal_loop") return <LoopIcon />;
  return <OutflowIcon />;
};

export const SinkNodeRF = memo(({ id, data, selected }: NodeProps<ValueFlowNodeData>) => {
  const [imageError, setImageError] = useState(false);
  const { hoveredNodeId } = useHoverContext();
  const isHovered = hoveredNodeId === id;
  const originalData = data.originalData as { sink_type?: string };
  const sinkType = originalData?.sink_type;
  const isLoop = sinkType === "internal_loop";
  const isMinimal = data.display === "minimal";
  const isHidden = isMinimal && data.label.trim().length === 0 && !data.icon?.spec_icon;

  if (isMinimal) {
    // Standard nodes are 80px (2/3 of core's 120px)
    const scale = data.showLabel === false ? Math.min(data.scale ?? 1, 1) : 1;
    const iconSize = Math.max(32, Math.round(80 * scale));
    const fallbackSize = Math.max(24, Math.round(54 * scale));
    const wrapperClass =
      data.showLabel === false
        ? "flex items-center justify-center relative"
        : "flex flex-col items-center gap-2 relative";
    const hiddenHandleClass = `!opacity-0 !w-2 !h-2 ${isLoop ? "!bg-indigo-300" : "!bg-sa-border"}`;
    return (
      <div
        data-testid={`value-flow-node-sink-${id}`}
        className={isHidden ? "opacity-0 w-2 h-2" : wrapperClass}
        style={
          !isHidden && data.showLabel === false ? { width: iconSize, height: iconSize } : undefined
        }
      >
        {/* Hover components - only show if not hidden */}
        {!isHidden && (
          <>
            <NodeHoverLabel name={data.label} description={data.description} visible={isHovered} />
            <NodeMetadataTooltip
              originalData={data.originalData}
              nodeCategory="sink"
              isHovered={isHovered}
            />
          </>
        )}
        {/* All 4 target handles */}
        <Handle type="target" position={Position.Top} id="top" className={hiddenHandleClass} />
        <Handle
          type="target"
          position={Position.Right}
          id="right-in"
          className={hiddenHandleClass}
        />
        <Handle
          type="target"
          position={Position.Bottom}
          id="bottom-in"
          className={hiddenHandleClass}
        />
        <Handle type="target" position={Position.Left} id="left-in" className={hiddenHandleClass} />
        {/* All 4 source handles */}
        <Handle type="source" position={Position.Top} id="top-out" className={hiddenHandleClass} />
        <Handle type="source" position={Position.Right} id="right" className={hiddenHandleClass} />
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          className={hiddenHandleClass}
        />
        <Handle type="source" position={Position.Left} id="left" className={hiddenHandleClass} />

        {!isHidden && (
          <>
            <div className="relative flex items-center justify-center">
              {data.icon?.spec_icon && !imageError ? (
                <img
                  src={resolveSpecIconUrl(data.icon.spec_icon)}
                  alt=""
                  className="object-contain"
                  style={{ width: iconSize, height: iconSize }}
                  onError={() => setImageError(true)}
                />
              ) : (
                <div
                  className={isLoop ? "text-indigo-400" : "text-sa-text"}
                  style={{ width: fallbackSize, height: fallbackSize }}
                >
                  {getIcon(sinkType)}
                </div>
              )}
              {/* Observable badges from schema ui_hint */}
              {data.observableBadges && data.observableBadges.length > 0 && (
                <ObservableBadgesContainer
                  badges={data.observableBadges}
                  position="top-right"
                  size="sm"
                />
              )}
            </div>
            {data.showLabel !== false && data.label.trim().length > 0 && (
              <div className="text-base text-sa-text text-center leading-tight max-w-[240px] break-words whitespace-normal font-medium">
                {data.label}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  const handleClass = `!w-2.5 !h-2.5 ${isLoop ? "!bg-indigo-400 !border-indigo-600" : "!bg-sa-text-dim !border-sa-border"}`;

  return (
    <div
      data-testid={`value-flow-node-sink-${id}`}
      className={`
          px-3 py-2 rounded-lg border-2 min-w-[150px]
          ${isLoop ? "bg-gradient-to-br from-indigo-500/10 to-purple-500/10" : "bg-gradient-to-br from-sa-surface/80 to-sa-surface/60"}
          ${
            selected
              ? isLoop
                ? "border-indigo-400 shadow-lg shadow-indigo-500/30"
                : "border-sa-border shadow-lg"
              : isLoop
                ? "border-indigo-500/40"
                : "border-sa-border/60"
          }
          transition-all duration-200
        `}
    >
      {/* All 4 target handles */}
      <Handle type="target" position={Position.Top} id="top" className={handleClass} />
      <Handle type="target" position={Position.Right} id="right-in" className={handleClass} />
      <Handle type="target" position={Position.Bottom} id="bottom-in" className={handleClass} />
      <Handle type="target" position={Position.Left} id="left-in" className={handleClass} />
      {/* All 4 source handles */}
      <Handle type="source" position={Position.Top} id="top-out" className={handleClass} />
      <Handle type="source" position={Position.Right} id="right" className={handleClass} />
      <Handle type="source" position={Position.Bottom} id="bottom" className={handleClass} />
      <Handle type="source" position={Position.Left} id="left" className={handleClass} />

      <div className="flex items-center gap-2">
        {/* Icon */}
        <div
          className={`
              relative flex-shrink-0 w-8 h-8 rounded flex items-center justify-center
              ${isLoop ? "bg-indigo-500/10 text-indigo-400" : "bg-sa-surface-hover/50 text-sa-text"}
            `}
        >
          {data.icon?.spec_icon && !imageError ? (
            <img
              src={resolveSpecIconUrl(data.icon.spec_icon)}
              alt=""
              className="w-5 h-5 object-contain"
              onError={() => setImageError(true)}
            />
          ) : (
            getIcon(sinkType)
          )}
          {/* Observable badges from schema ui_hint */}
          {data.observableBadges && data.observableBadges.length > 0 && (
            <ObservableBadgesContainer
              badges={data.observableBadges}
              position="top-right"
              size="sm"
            />
          )}
        </div>

        {/* Label */}
        {data.showLabel !== false && (
          <div className="flex-1 min-w-0">
            <div
              className={`text-base font-medium leading-tight break-words whitespace-normal ${
                isLoop ? "text-indigo-400" : "text-sa-text"
              }`}
            >
              {data.label}
            </div>
            {data.description && (
              <div
                className={`text-sm leading-tight break-words whitespace-normal ${
                  isLoop ? "text-indigo-400/70" : "text-sa-text-dim"
                }`}
              >
                {data.description}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

SinkNodeRF.displayName = "SinkNodeRF";
